import { Link } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { rutaPrivacidad } from "@/lib/legalPaths";

export type FinalidadFormulario = "plaza" | "renovacion" | "campamento" | "cumpleanos";

interface Textos {
  titulo: string;
  responsable: string;
  finalidad: Record<FinalidadFormulario, string>;
  base: Record<FinalidadFormulario, string>;
  destinatarios: string;
  derechos: string;
  masInfo: string;
  politica: string;
  pestanaNueva: string;
  etiquetas: { responsable: string; finalidad: string; base: string; destinatarios: string; derechos: string };
}

const TEXTOS: Record<string, Textos> = {
  es: {
    titulo: "Protección de datos",
    responsable: "Noguera y Valdés, S.L. (Gándara International School).",
    finalidad: {
      plaza: "gestionar tu solicitud de plaza o tu inscripción en la jornada de puertas abiertas.",
      renovacion: "gestionar la renovación de matrícula, los servicios del curso y su cobro.",
      campamento: "gestionar la inscripción en el campamento y atender a tu hijo o hija durante la actividad.",
      cumpleanos: "gestionar la reserva del cumpleaños y organizar la fiesta.",
    },
    base: {
      plaza: "medidas precontractuales a tu petición.",
      renovacion: "ejecución del contrato y obligaciones legales.",
      campamento: "ejecución del contrato; los datos de salud, solo con tu consentimiento.",
      cumpleanos: "ejecución del contrato.",
    },
    destinatarios: "no se ceden datos salvo obligación legal o para gestionar los cobros; los tratan por nuestra cuenta proveedores tecnológicos.",
    derechos: "acceso, rectificación, supresión y otros, escribiendo a info@gandaraschool.com.",
    masInfo: "Más información en la",
    politica: "política de privacidad",
    pestanaNueva: "(se abre en una pestaña nueva)",
    etiquetas: { responsable: "Responsable", finalidad: "Finalidad", base: "Base legal", destinatarios: "Destinatarios", derechos: "Derechos" },
  },
  en: {
    titulo: "Data protection",
    responsable: "Noguera y Valdés, S.L. (Gándara International School).",
    finalidad: {
      plaza: "to handle your application for a place or your registration for the open day.",
      renovacion: "to handle the re-enrolment, the services for the school year and their payment.",
      campamento: "to handle the camp registration and look after your child during the activity.",
      cumpleanos: "to handle the birthday booking and organise the party.",
    },
    base: {
      plaza: "pre-contractual steps at your request.",
      renovacion: "performance of the contract and legal obligations.",
      campamento: "performance of the contract; health information, only with your consent.",
      cumpleanos: "performance of the contract.",
    },
    destinatarios: "data is not disclosed unless required by law or to handle payments; technology providers process it on our behalf.",
    derechos: "access, rectification, erasure and others, by writing to info@gandaraschool.com.",
    masInfo: "More information in the",
    politica: "privacy policy",
    pestanaNueva: "(opens in a new tab)",
    etiquetas: { responsable: "Controller", finalidad: "Purpose", base: "Legal basis", destinatarios: "Recipients", derechos: "Rights" },
  },
  pt: {
    titulo: "Proteção de dados",
    responsable: "Noguera y Valdés, S.L. (Gándara International School).",
    finalidad: {
      plaza: "gerir o seu pedido de vaga ou a sua inscrição no dia de portas abertas.",
      renovacion: "gerir a renovação da matrícula, os serviços do ano letivo e o seu pagamento.",
      campamento: "gerir a inscrição no acampamento e cuidar do seu filho ou da sua filha durante a atividade.",
      cumpleanos: "gerir a reserva do aniversário e organizar a festa.",
    },
    base: {
      plaza: "diligências pré-contratuais a seu pedido.",
      renovacion: "execução do contrato e obrigações legais.",
      campamento: "execução do contrato; os dados de saúde, apenas com o seu consentimento.",
      cumpleanos: "execução do contrato.",
    },
    destinatarios: "os dados não são cedidos salvo obrigação legal ou para gerir as cobranças; são tratados por nossa conta por fornecedores tecnológicos.",
    derechos: "acesso, retificação, apagamento e outros, escrevendo para info@gandaraschool.com.",
    masInfo: "Mais informação na",
    politica: "política de privacidade",
    pestanaNueva: "(abre num novo separador)",
    etiquetas: { responsable: "Responsável", finalidad: "Finalidade", base: "Fundamento jurídico", destinatarios: "Destinatários", derechos: "Direitos" },
  },
  fr: {
    titulo: "Protection des données",
    responsable: "Noguera y Valdés, S.L. (Gándara International School).",
    finalidad: {
      plaza: "gérer votre demande de place ou votre inscription à la journée portes ouvertes.",
      renovacion: "gérer la réinscription, les services de l'année scolaire et leur paiement.",
      campamento: "gérer l'inscription au camp et s'occuper de votre enfant pendant l'activité.",
      cumpleanos: "gérer la réservation de l'anniversaire et organiser la fête.",
    },
    base: {
      plaza: "mesures précontractuelles prises à votre demande.",
      renovacion: "exécution du contrat et obligations légales.",
      campamento: "exécution du contrat ; les données de santé, uniquement avec votre consentement.",
      cumpleanos: "exécution du contrat.",
    },
    destinatarios: "aucune donnée n'est communiquée sauf obligation légale ou pour gérer les paiements ; des prestataires techniques les traitent pour notre compte.",
    derechos: "accès, rectification, effacement et autres, en écrivant à info@gandaraschool.com.",
    masInfo: "Plus d'informations dans la",
    politica: "politique de confidentialité",
    pestanaNueva: "(s'ouvre dans un nouvel onglet)",
    etiquetas: { responsable: "Responsable", finalidad: "Finalité", base: "Base juridique", destinatarios: "Destinataires", derechos: "Droits" },
  },
};

/** Información básica de protección de datos junto a cada formulario (primera capa, art. 11 LOPDGDD) */
export const DataProtectionNotice = ({ finalidad }: { finalidad: FinalidadFormulario }) => {
  const { language } = useLanguage();
  const t = TEXTOS[language] || TEXTOS.es;
  const filas: [string, string][] = [
    [t.etiquetas.responsable, t.responsable],
    [t.etiquetas.finalidad, t.finalidad[finalidad]],
    [t.etiquetas.base, t.base[finalidad]],
    [t.etiquetas.destinatarios, t.destinatarios],
    [t.etiquetas.derechos, t.derechos],
  ];
  return (
    <div role="note" aria-label={t.titulo} className="rounded-md border border-border bg-muted/40 p-3 text-xs leading-relaxed text-muted-foreground">
      <p className="mb-1 font-medium text-foreground">{t.titulo}</p>
      {filas.map(([etiqueta, texto]) => (
        <p key={etiqueta}>
          <span className="font-medium">{etiqueta}:</span> {texto}
        </p>
      ))}
      <p>
        {t.masInfo}{" "}
        <Link to={rutaPrivacidad(language)} target="_blank" className="text-primary underline underline-offset-4">
          {t.politica}
          <span className="sr-only"> {t.pestanaNueva}</span>
        </Link>
        .
      </p>
    </div>
  );
};
